import axios from "axios";
import { ENV } from "../configs/environment";
import { getParams } from "../utlis/helpers";
import store from "../state/store";
import { setAPILoader, setErrorDisplay } from "../state/actions/globalAction";
import { getSigninUrl } from "../configs/environment";
import { logoOut, getLiveToken } from "./websiteApi";
import { pageLocale } from "../utlis/locale";
import { postWebViewMessage } from "../utlis/webViewBridge";

// Public axios instance — no auth interceptors (used for unauthenticated endpoints)
const publicApi = axios.create({ baseURL: ENV.API_URL });

export const verifyRecipientEmail = async (token) => {
  const response = await publicApi.post("v5/report/recipientVerification", {
    token,
  });
  return response.data;
};

const { errorCode } = pageLocale("common");

// Create axios instance for V5 API
const v5Api = axios.create({
  baseURL: ENV.API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token and locale
v5Api.interceptors.request.use(
  (config) => {
    // Resolve the token from the shared source used by websiteApi: URL params
    // first, then the persisted mmtSession. This keeps auth working even when
    // the current screen's URL has dropped the accessToken query param.
    const token = getLiveToken();
    const { locale } = getParams();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Forward user's locale so backend can return localized content
    config.headers["Accept-Language"] = locale || "en";

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling (matching websiteApi.js pattern)
v5Api.interceptors.response.use(
  (response) => {
    // Check for custom status 0 if that's a pattern, though V5 docs say 200/201 for success
    if (response.data && response.data.status === 0) {
      return Promise.reject(new Error(response.data.message || "API Error"));
    }
    return response;
  },
  (error) => {
    const { accessToken, locale } = getParams();

    if (error.response?.status === 401 || error.response?.status === 403) {
      // Handle unauthorized access similar to websiteApi.js
      store.dispatch(setErrorDisplay(errorCode));
      store.dispatch(setAPILoader(true));
      logoOut({ globalSignOut: false, token: accessToken })
        .then(() => {
          sessionStorage.clear();
          // Notify React Native WebView; skip redirect if inside WebView
          if (!postWebViewMessage("TOKEN_INVALID")) {
            window.location.href = getSigninUrl(locale || "en");
          }
          store.dispatch(setAPILoader(false));
        })
        .catch(() => {
          store.dispatch(setAPILoader(false));
        });
    } else {
      // For other errors, just return the error info but don't necessarily dispatch global error display
      // unless we want to force it? keeping it local for now to let caller handle or dispatch
      // Actually websiteApi.js dispatches error, let's keep consistency if needed,
      // but explicit error handling in components is often better.
      // For now, let's return the simplified error message
    }
    // For other errors, return the error info but let caller handle
    return Promise.reject(error);
  },
);

/**
 * Vitals Management
 */

// Create Vitals
export const createVitals = async (vitalsData, seniorId) => {
  let url = "/v5/vitals";
  if (seniorId) {
    url += `?seniorId=${seniorId}`;
  }
  const response = await v5Api.post(url, vitalsData);
  return response.data;
};

// List Vitals
export const listVitals = async (seniorId) => {
  let url = "/v5/vitals";
  if (seniorId) {
    url += `?seniorId=${seniorId}`;
  }
  const response = await v5Api.get(url);
  return response.data;
};

// Get Vitals by ID
export const getVitalsById = async (id) => {
  const response = await v5Api.get(`/v5/vitals/${id}`);
  return response.data;
};

/**
 * Risk Assessment
 */

// Get Questionnaire
export const getQuestionnaire = async (seniorId) => {
  const response = await v5Api.get(
    `/v5/risk-assessment/questionnaire?seniorId=${seniorId}`,
  );
  return response.data;
};

// Submit Questionnaire
export const submitQuestionnaire = async (responses, personId) => {
  const response = await v5Api.post(
    `/v5/risk-assessment/submit?seniorId=${personId}`,
    responses,
  );
  return response.data;
};

// List Risk Assessments
export const listRiskAssessments = async (personId) => {
  const response = await v5Api.get(
    `/v5/risk-assessment/list?seniorId=${personId}`,
  );
  return response.data;
};

// Get Risk Assessment by ID
export const getRiskAssessmentById = async (id, seniorId) => {
  const response = await v5Api.get(
    `/v5/risk-assessment/summary/${id}?seniorId=${seniorId}`,
  );
  return response.data;
};

/**
 * Care Plan
 */

// Get Care Plan for a senior
export const getCarePlan = async (seniorUuid) => {
  const response = await v5Api.get(`/v5/seniors/${seniorUuid}/care-plan`);
  return response.data;
};

/**
 * Assets Management
 */

// Get Asset Token
export const getAssetToken = async (path, moduleId, seniorId) => {
  const response = await v5Api.get(
    `/v5/assets/token?path=${path}&moduleId=${moduleId}&seniorId=${seniorId}&storage=gcp`,
  );
  return response.data;
};

// Construct Video URL with SAS Token
export const getVideoUrl = async (path, moduleId, seniorId) => {
  try {
    const tokenResponse = await getAssetToken(path, moduleId, seniorId);
    // Adjust based on actual API response structure
    const token = tokenResponse?.items || tokenResponse?.token || tokenResponse;
    const accountName = ENV.AZURE_STORAGE_ACCOUNT_NAME;
    const containerName = ENV.AZURE_STORAGE_CONTAINER_NAME;

    if (!token) {
      console.warn("No token received for asset:", path);
      return null;
    }

    return `https://${accountName}.blob.core.windows.net/${containerName}/${path}?${token}`;
  } catch (error) {
    console.error("Error constructing video URL:", error);
    return null;
  }
};

export default v5Api;
